import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';

import { JBEnvironmentId, JbNavigationWizardMainSection } from '@bjb-gcrm/gcrm-frontend-components';
import { ZfStack } from '@zafire/react';
import { GlobalState } from 'reducers/stateTypes';
import { BrTabItems } from 'views/BrTabs/BrTabs';
import { PcoNavigationWrapperWC } from '@juliusbaer-gcrm/pco-product-service-components';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { NavigationWizardModeStateMachineOutput } from 'views/BrTabs/types/navigationWizardMode';
import { CoreDataSection } from 'views/BrTabs/components/ProfilingSections/CoreDataSection';
import { BrRelationsSection } from 'views/BrTabs/components/ProfilingSections/BrRelationsSection';
import { BrPurposeSection } from 'views/BrTabs/components/ProfilingSections/BrPurposeSection';
import { ServicingUsersSection } from 'views/BrTabs/components/ProfilingSections/ServicingUsersSection';
import { DeliveryInstructionsNavigationWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/DeliveryInstructionsNavigationWrapperWC';
import { BlockingInstructionsNavigationWrapperWC } from '@juliusbaer-gcrm/pcm-br-mgmt-components/BlockingInstructionsNavigationWrapperWC';
import { NavigationSectionGroup } from 'views/NavigationWizard/components/NavigationSectionGroup';

type Props = {
  env: JBEnvironmentId;
  onNavigate: (tabId: BrTabItems) => void;
  navigationWizardMode?: NavigationWizardModeStateMachineOutput;
};

export const NavigationContent = ({ env, onNavigate, navigationWizardMode = { type: 'full' } }: Props) => {
  const { t } = useTranslation();
  const selectedTab = useSelector((state: GlobalState) => state.brTabs.tab);
  const runtimeConfig = useRuntimeConfig();
  const showPcoNavigationWC = runtimeConfig.configData.featureFlags?.pcoWizardWC?.enabled;
  const showPortfolioNavigation = runtimeConfig.configData.featureFlags?.pcoPortfolio?.enabled;
  const { sections } = navigationWizardMode;

  const handleToggleMain = (id: string) => {
    if (id === selectedTab) {
      return;
    }
    onNavigate(id as BrTabItems);
  };

  if (navigationWizardMode.type === 'hidden') {
    return null;
  }

  if (navigationWizardMode.type === 'legacy') {
    return (
      <NavigationSectionGroup title={t('navigationWizard.section.title.profile')}>
        <BrPurposeSection selectedTab={selectedTab} navigateToTab={() => handleToggleMain(BrTabItems.BR_PURPOSE)} />
      </NavigationSectionGroup>
    );
  }

  if (navigationWizardMode.type === 'reduced') {
    return (
      <>
        <NavigationSectionGroup title={t('navigationWizard.section.title.general')}>
          <CoreDataSection selectedTab={selectedTab} navigateToTab={() => handleToggleMain(BrTabItems.CORE_DATA)} />
        </NavigationSectionGroup>
        <NavigationSectionGroup title={t('navigationWizard.section.title.relationships')}>
          <BrRelationsSection
            selectedTab={selectedTab}
            navigateToTab={() => handleToggleMain(BrTabItems.BR_RELATIONS)}
          />
          <ServicingUsersSection
            selectedTab={selectedTab}
            navigateToTab={() => handleToggleMain(BrTabItems.BR_SERVICING)}
          />
        </NavigationSectionGroup>
        <NavigationSectionGroup title={t('navigationWizard.section.title.profile')}>
          <BrPurposeSection selectedTab={selectedTab} navigateToTab={() => handleToggleMain(BrTabItems.BR_PURPOSE)} />
        </NavigationSectionGroup>
      </>
    );
  }

  const productsAndServicesSection = (
    <JbNavigationWizardMainSection
      id={BrTabItems.PRODUCTS_AND_SERVICES}
      title="Products"
      subSections={undefined}
      isActive={selectedTab === BrTabItems.PRODUCTS_AND_SERVICES}
      isLoading={false}
      onSectionSelect={() => handleToggleMain(BrTabItems.PRODUCTS_AND_SERVICES)}
      isProfilingRelevant={false}
    />
  );

  return (
    <ZfStack spacing="medium">
      <NavigationSectionGroup title={t('navigationWizard.section.title.general')}>
        <CoreDataSection selectedTab={selectedTab} navigateToTab={() => handleToggleMain(BrTabItems.CORE_DATA)} />
      </NavigationSectionGroup>
      <NavigationSectionGroup title={t('navigationWizard.section.title.relationships')}>
        <BrRelationsSection selectedTab={selectedTab} navigateToTab={() => handleToggleMain(BrTabItems.BR_RELATIONS)} />
        <ServicingUsersSection
          selectedTab={selectedTab}
          navigateToTab={() => handleToggleMain(BrTabItems.BR_SERVICING)}
        />
      </NavigationSectionGroup>
      <NavigationSectionGroup title={t('navigationWizard.section.title.profile')}>
        <BrPurposeSection selectedTab={selectedTab} navigateToTab={() => handleToggleMain(BrTabItems.BR_PURPOSE)} />
        <ErrorBoundary
          fallback={
            <JbNavigationWizardMainSection
              id={BrTabItems.BR_DELIVERY_INSTRUCTIONS}
              title={t('navigationWizard.item.title.deliveryInstructions')}
              subSections={undefined}
              isActive={selectedTab === BrTabItems.BR_DELIVERY_INSTRUCTIONS}
              isLoading={false}
              onSectionSelect={() => handleToggleMain(BrTabItems.BR_DELIVERY_INSTRUCTIONS)}
              isProfilingRelevant={false}
            />
          }
        >
          <DeliveryInstructionsNavigationWrapperWC
            env={env}
            version="0.0.1"
            onSectionSelect={() => handleToggleMain(BrTabItems.BR_DELIVERY_INSTRUCTIONS)}
            isActive={selectedTab === BrTabItems.BR_DELIVERY_INSTRUCTIONS}
          />
        </ErrorBoundary>

        <JbNavigationWizardMainSection
          id={BrTabItems.RISK_CONSIDERATIONS}
          title={t('navigationWizard.item.title.riskConsiderations')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.RISK_CONSIDERATIONS}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.RISK_CONSIDERATIONS)}
          isProfilingRelevant={false}
        />
        <JbNavigationWizardMainSection
          id={BrTabItems.INVESTMENT_PROFILE}
          title={t('navigationWizard.item.title.investmentProfile')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.INVESTMENT_PROFILE}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.INVESTMENT_PROFILE)}
          isProfilingRelevant={false}
        />
        <JbNavigationWizardMainSection
          id={BrTabItems.CLIENT_REVIEW}
          title={t('navigationWizard.item.title.clientReview')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.CLIENT_REVIEW}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.CLIENT_REVIEW)}
          isProfilingRelevant={false}
        />
        {showPcoNavigationWC && (
          <JbNavigationWizardMainSection
            id={'waivers'}
            title={t('navigationWizard.item.title.waivers')}
            subSections={[]}
            activeSubSection={selectedTab}
            isActive={selectedTab === BrTabItems.COUNTRY_RISK_WAIVERS}
            isLoading={false}
            onSectionSelect={() => handleToggleMain(BrTabItems.COUNTRY_RISK_WAIVERS)}
            isProfilingRelevant={false}
          />
        )}
        <ErrorBoundary
          fallback={
            <JbNavigationWizardMainSection
              id={BrTabItems.BLOCKING_INSTRUCTIONS}
              title={t('navigationWizard.item.title.blockingInstructions')}
              subSections={undefined}
              isActive={selectedTab === BrTabItems.BLOCKING_INSTRUCTIONS}
              isLoading={false}
              onSectionSelect={() => handleToggleMain(BrTabItems.BLOCKING_INSTRUCTIONS)}
              isProfilingRelevant={false}
            />
          }
        >
          <BlockingInstructionsNavigationWrapperWC
            env={env}
            version="0.0.1"
            onSectionSelect={() => handleToggleMain(BrTabItems.BLOCKING_INSTRUCTIONS)}
            isActive={selectedTab === BrTabItems.BLOCKING_INSTRUCTIONS}
          />
        </ErrorBoundary>
      </NavigationSectionGroup>
      <NavigationSectionGroup title={t('navigationWizard.section.title.products')}>
        {showPcoNavigationWC ? (
          <ErrorBoundary fallback={productsAndServicesSection}>
            <PcoNavigationWrapperWC
              env={env}
              version="0.0.1"
              onSectionSelect={() => handleToggleMain(BrTabItems.PRODUCTS_AND_SERVICES)}
              isActive={selectedTab === BrTabItems.PRODUCTS_AND_SERVICES}
            />
          </ErrorBoundary>
        ) : (
          productsAndServicesSection
        )}
        {showPortfolioNavigation && (
          <JbNavigationWizardMainSection
            id={BrTabItems.PORTFOLIOS}
            title={t('navigationWizard.section.title.portfolioCashAccounts')}
            subSections={undefined}
            isActive={selectedTab === BrTabItems.PORTFOLIOS}
            isLoading={false}
            onSectionSelect={() => handleToggleMain(BrTabItems.PORTFOLIOS)}
            isProfilingRelevant={false}
          />
        )}
      </NavigationSectionGroup>
      <NavigationSectionGroup title={t('navigationWizard.section.title.contactNotes')}>
        <JbNavigationWizardMainSection
          id={BrTabItems.BR_CONTACT_NOTES}
          title={t('navigationWizard.item.title.brContactNotes')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.BR_CONTACT_NOTES}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.BR_CONTACT_NOTES)}
          isProfilingRelevant={false}
        />
      </NavigationSectionGroup>
      <NavigationSectionGroup title={t('navigationWizard.section.title.documents')}>
        <JbNavigationWizardMainSection
          id={BrTabItems.DISTRIBUTION}
          title={t('navigationWizard.item.title.documents.distribution')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.DISTRIBUTION}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.DISTRIBUTION)}
          isProfilingRelevant={false}
        />
        <JbNavigationWizardMainSection
          id={BrTabItems.DOCUMENT_OVERVIEW}
          title={t('navigationWizard.item.title.documents.overview')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.DOCUMENT_OVERVIEW}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.DOCUMENT_OVERVIEW)}
          isProfilingRelevant={false}
        />
        <JbNavigationWizardMainSection
          id={BrTabItems.DOC_COLLECTION}
          title={t('navigationWizard.item.title.documents.collection')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.DOC_COLLECTION}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.DOC_COLLECTION)}
          isProfilingRelevant={false}
        />
      </NavigationSectionGroup>
      <NavigationSectionGroup title={t('navigationWizard.section.title.reporting')}>
        <JbNavigationWizardMainSection
          id={BrTabItems.REPORTING}
          title={t('navigationWizard.item.title.documents.reporting')}
          subSections={undefined}
          isActive={selectedTab === BrTabItems.REPORTING}
          isLoading={false}
          onSectionSelect={() => handleToggleMain(BrTabItems.REPORTING)}
          isProfilingRelevant={false}
        />
      </NavigationSectionGroup>
    </ZfStack>
  );
};
